/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview PaymentModal FooterActions step UI.
 * @module modals/PaymentModal/steps/PaymentModalFooterActions
 */

import CircularProgress from "@mui/material/CircularProgress";
import { validateRFC } from "../../../utils/common.js";

const PaymentModalFooterActions = ({
  styles,
  t,
  stepNumber,
  setStepNumber,
  paymentMethod,
  setPaymentMethod,
  paymentStatus,
  formData,
  setRfcFormatError,
  isStep1Valid,
  isStep2Valid,
  isStep3Valid,
  onClose,
  cashPaymentLoading,
  handleCashPayment,
  cardPaymentLoading,
  handleCardPayment,
}) => (
  <>
    {stepNumber === 1 && (
          <div className={styles.formActions}>
            <div>
              <button
                className={styles.cancelButton}
                type="button"
                disabled={false}
                onClick={onClose}
              >
                {t("common.cancel")}
              </button>
              <button
                className={styles.nextButton}
                type="button"
                disabled={!isStep1Valid()}
                onClick={() => {
                  const rfcValue = formData.RFC?.trim() ?? "";
                  if (rfcValue) {
                    const result = validateRFC(rfcValue);
                    setRfcFormatError(result.isValid ? null : result.error);
                  }
                  if (isStep1Valid()) {
                    setStepNumber(stepNumber + 1);
                  }
                }}
              >
                {t("common.next")}
              </button>
            </div>
          </div>
        )}

        {/* The selected payment method is cash */}
        {stepNumber === 2 && paymentMethod === "cash" && (
          <div className={styles.formActions}>
            <div>
              <button
                className={styles.previousButton}
                type="button"
                disabled={cashPaymentLoading}
                onClick={() => {
                  setStepNumber(stepNumber - 1);
                  setPaymentMethod("cash");
                }}
              >
                {t("common.previous")}
              </button>
              <button
                className={styles.nextButton}
                type="button"
                disabled={!isStep2Valid() || cashPaymentLoading}
                onClick={handleCashPayment}
              >
                {cashPaymentLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("common.pay")
                )}
              </button>
            </div>
          </div>
        )}

        {/* The selected payment method is card */}
        {stepNumber === 2 && paymentMethod === "card" && (
          <div className={styles.formActions}>
            <div>
              <button
                className={styles.previousButton}
                type="button"
                disabled={false}
                onClick={() => {
                  setStepNumber(stepNumber - 1);
                  setPaymentMethod("cash");
                }}
              >
                {t("common.previous")}
              </button>
              <button
                className={styles.nextButton}
                type="button"
                disabled={!isStep2Valid()}
                onClick={() => {
                  setStepNumber(stepNumber + 1);
                  if (isStep2Valid()) {
                    setStepNumber(stepNumber + 1);
                  }
                }}
              >
                {t("common.next")}
              </button>
            </div>
          </div>
        )}

        {/* The card payment details are being read */}
        {stepNumber === 3 && paymentMethod === "card" && (
          <div className={styles.formActions}>
            <div>
              <button
                className={styles.previousButton}
                type="button"
                disabled={false}
                onClick={() => {
                  setStepNumber(stepNumber - 1);
                  setPaymentMethod("card");
                }}
              >
                {t("common.cancel")}
              </button>
              <button
                className={styles.nextButton}
                type="button"
                disabled={!isStep3Valid() || cardPaymentLoading}
                onClick={handleCardPayment}
              >
                {cardPaymentLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("common.confirmPayment")
                )}
              </button>
            </div>
          </div>
        )}

        {/* The card payment was declined, the user can retry the payment */}
        {stepNumber === 4 &&
          paymentMethod === "card" &&
          paymentStatus === "PAYMENT_ERROR" && (
            <div className={styles.formActions}>
              <div>
                <button
                  className={styles.previousButton}
                  type="button"
                  disabled={false}
                  onClick={() => {
                    setStepNumber(stepNumber - 2);
                    setPaymentMethod("card");
                  }}
                >
                  {t("common.modifyData")}
                </button>
                <button
                  className={styles.nextButton}
                  type="button"
                  disabled={!isStep3Valid() || cardPaymentLoading}
                  onClick={handleCardPayment}
                >
                  {cardPaymentLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    t("common.retryPayment")
                  )}
                </button>
              </div>
            </div>
          )}
  </>
);

export default PaymentModalFooterActions;
